import { Component, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/auth/auth.service';
import { NotificationService } from '../../core/services/notification.service';
import { DevOpsTicket } from '../../models/devops-ticket.model';

export interface TicketDetailData {
  ticket: DevOpsTicket;
}

@Component({
  selector: 'app-ticket-detail-dialog',
  standalone: true,
  imports: [DatePipe, FormsModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './ticket-detail-dialog.component.html',
  styleUrl: './ticket-detail-dialog.component.scss',
})
export class TicketDetailDialogComponent {
  readonly data   = inject<TicketDetailData>(MAT_DIALOG_DATA);
  private readonly api    = inject(ApiService);
  private readonly auth   = inject(AuthService);
  private readonly notify = inject(NotificationService);
  private readonly ref    = inject(MatDialogRef<TicketDetailDialogComponent>);

  ticket           = signal<DevOpsTicket>({ ...this.data.ticket });
  downloading      = signal(false);
  downloadingMd    = signal(false);

  // Approve / reject panel
  approvalAction   = signal<'approve' | 'reject' | null>(null);
  rejectionReason  = signal('');
  estimatedCost    = signal('');
  actioning        = signal(false);

  get isSetupNewServices(): boolean { return this.ticket().sub_type === 'Setup New Services'; }
  get isCicd(): boolean             { return this.ticket().sub_type === 'Setup CI/CD'; }
  get isGcpAccess(): boolean        { return this.ticket().sub_type === 'Access to GCP Project'; }
  get isFileUpload(): boolean       { return this.ticket().sub_type === 'File Upload Exceptions'; }
  get isDnsMapping(): boolean       { return this.ticket().sub_type === 'DNS Mapping'; }

  get firstService() { return this.ticket().services?.[0]; }

  get canApproveReject(): boolean {
    if (!this.canEdit) return false;
    const s = this.ticket().status;
    return s === 'Open' || s === 'In Progress';
  }

  get approveDisabled(): boolean {
    if (this.actioning()) return true;
    if (this.isSetupNewServices && !this.estimatedCostNum()) return true;
    return false;
  }

  estimatedCostNum = signal<number | null>(null);

  formatCost(raw: string | null): string {
    if (!raw) return '';
    const n = Number(raw.replace(/[^0-9.]/g, ''));
    if (!isNaN(n) && n > 0) {
      return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 }) + '/month';
    }
    return raw;
  }

  get rejectDisabled(): boolean {
    return this.actioning() || !this.rejectionReason().trim();
  }

  openApproval(action: 'approve' | 'reject'): void {
    if (this.approvalAction() === action) {
      this.approvalAction.set(null);
    } else {
      this.approvalAction.set(action);
      this.rejectionReason.set('');
      this.estimatedCost.set('');
    }
  }

  confirmAction(): void {
    const action = this.approvalAction();
    if (!action || this.actioning()) return;
    this.actioning.set(true);

    const body: Record<string, unknown> = { action };
    if (action === 'reject') body['rejection_reason'] = this.rejectionReason();
    if (action === 'approve' && this.isSetupNewServices) body['estimated_cost'] = String(this.estimatedCostNum() ?? '');

    this.api.patch<DevOpsTicket>(`/api/tickets/${this.ticket().id}`, body).subscribe({
      next: (updated) => {
        this.ticket.set(updated);
        this.notify.success(action === 'approve' ? 'Request approved' : 'Request rejected');
        this.actioning.set(false);
        this.approvalAction.set(null);
        this.ref.close(true);
      },
      error: (err) => {
        this.notify.error(err?.error?.error ?? 'Action failed');
        this.actioning.set(false);
      },
    });
  }

  downloadTerraform(): void {
    if (this.downloading()) return;
    this.downloading.set(true);
    this.api.getBlob(`/api/tickets/${this.ticket().id}/terraform`).subscribe({
      next: (blob) => {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = `terraform_ticket_${this.ticket().id}.tf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.downloading.set(false);
      },
      error: (err) => {
        const blobErr = err?.error;
        if (blobErr instanceof Blob) {
          blobErr.text().then(text => {
            try {
              const parsed = JSON.parse(text);
              this.notify.error(parsed?.error ?? 'Failed to download Terraform script');
            } catch {
              this.notify.error('Failed to download Terraform script');
            }
          });
        } else {
          this.notify.error(err?.error?.error ?? 'Failed to download Terraform script');
        }
        this.downloading.set(false);
      },
    });
  }

  downloadMarkdown(): void {
    if (this.downloadingMd()) return;
    this.downloadingMd.set(true);
    this.api.getBlob(`/api/tickets/${this.ticket().id}/markdown`).subscribe({
      next: (blob) => {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = `ticket_${this.ticket().id}_services.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.downloadingMd.set(false);
      },
      error: (err) => {
        const blobErr = err?.error;
        if (blobErr instanceof Blob) {
          blobErr.text().then(text => {
            try {
              const parsed = JSON.parse(text);
              this.notify.error(parsed?.error ?? 'Failed to download Markdown');
            } catch {
              this.notify.error('Failed to download Markdown');
            }
          });
        } else {
          this.notify.error(err?.error?.error ?? 'Failed to download Markdown');
        }
        this.downloadingMd.set(false);
      },
    });
  }

  get canEdit(): boolean {
    const me = this.auth.currentUser();
    if (!me) return false;
    return (
      this.auth.isAdmin ||
      this.ticket().assigned_to?.id === (me as any).id
    );
  }

  priorityClass(p: string): string {
    return { High: 'chip--high', Medium: 'chip--med', Low: 'chip--low' }[p] ?? '';
  }

  statusClass(s: string): string {
    return {
      'Open':        'chip--open',
      'In Progress': 'chip--inprog',
      'Approved':    'chip--approved',
      'Rejected':    'chip--rejected',
      'Resolved':    'chip--resolved',
      'Closed':      'chip--closed',
    }[s] ?? '';
  }
}
